import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {Component} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {CpmCJobsInstanceApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-jobs-instance.api-model';
import {
    ETableColDataType,
    ETableColFilterType,
    TableTagDef
} from '../../../../sdk/src/components/generic-table/table-col-def';
import {IBaseCRUModalXExtendedNone} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CpmCJobsInstanceApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-jobs-instance.api-service';
import {ECpmCJobsInstanceStatusApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/c-global/cpmc-jobs-instance.api-enums';
import {ECpmCJobsRoleApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk-jobs.api-enums';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

/**
 * JobsInstance CRUD table component
 */
@Component({
    selector: 'app-jobs-instance-crud',
    templateUrl: './jobs-instance-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class JobsInstanceCRUDComponent extends IBaseCRUDComponent<
    CpmCJobsInstanceApiModel,
    unknown,
    IBaseCRUModalXExtendedNone,
    IBaseCRUModalXExtendedNone
> {
    protected helpVisible = false;
    protected rolesTags: TableTagDef[] = Object.keys(ECpmCJobsRoleApiEnum).map((x) => ({
        style: {background: this.generateColorFromString(x)},
        label: x,
        value: x
    }));
    protected statusDescriptions: Record<string, string> = {
        ['UNREACHABLE']: 'Unreachable',
        ['INCOMPATIBLE']: 'The instance is reporting incompatible specification',
        ['IDLE']: 'Reachable and waiting for work',
        ['ACTIVE']: 'Reachable and doing work',
        ['CONFLICT']: 'The instance is causing conflict on the same InstanceUUID'
    };
    protected statusTags: TableTagDef[] = Object.keys(ECpmCJobsInstanceStatusApiEnum).map((x) => ({
        style: {background: this.generateColorFromString(x)},
        label: x,
        value: x,
        tooltip: this.statusDescriptions[x]
    }));

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'JobsInstanceApiModel',
            getModelHumanIdentifier: (model: CpmCJobsInstanceApiModel) => model.name ?? '<Unknown Jobs Instance>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: undefined,

            // Modal config
            modalType: null,
            getDialogServiceParameters: null,
            modalExtendedConfigBuilder: null,

            // Authorization config
            authorizationNew: null,
            authorizationEdit: null,
            authorizationDelete: null
        });

        this.modelHumanName = 'Jobs Instance';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading jobs instances...';

        const result = await firstValueFrom(CpmCJobsInstanceApiService.getJobsInstanceList({}, this.paginatorConfig));

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'job_role',
                header: 'Role',
                dataType: ETableColDataType.TAGS,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.rolesTags,
                tags: this.rolesTags
            },
            {
                field: 'status',
                header: 'Status',
                dataType: ETableColDataType.TAGS,
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.statusTags,
                tags: this.statusTags
            },
            {
                field: 'instance_uuid',
                header: 'Instance UUID',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                tdStyle: {'font-family': 'monospace, monospace'}
            },
            {
                field: 'last_runtime_uuid',
                header: 'Runtime UUID',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                tdStyle: {'font-family': 'monospace, monospace'}
            }
        ];

        this.addCreatedColumns(false);
    }
}
